<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :title="title"
      :actions="actions"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>
    <v-dialog persistent v-model="dialog" max-width="920">
      <v-card class="voicemail-dialog-card">
        <div class="voicemail-dialog-header">
          <div class="voicemail-dialog-header-info">
            <div v-if="accountLinked" class="voicemail-dialog-title">
              <v-icon small color="white" class="mr-2">mdi-voicemail</v-icon>
              <span class="voicemail-dialog-title-en">{{
                rowClicked.name || rowClicked.accountID
              }}</span>
              <span v-if="rowClicked.hname" class="voicemail-dialog-title-he">{{
                rowClicked.hname
              }}</span>
            </div>
            <div
              v-else
              class="voicemail-dialog-title voicemail-dialog-title--missing"
            >
              <v-icon small color="#ffcc80" class="mr-2"
                >mdi-account-alert</v-icon
              >
              <span>Unknown account</span>
            </div>

            <div class="voicemail-dialog-subtitle">
              <span v-if="accountLinked"
                >Account {{ rowClicked.accountID }}</span
              >
              <span v-else>Not linked to an account</span>
              <span v-if="accountLinked && rowClicked.homePhone">
                &middot; Home {{ formatPhone(rowClicked.homePhone) }}
              </span>
              <span v-if="accountLinked && rowClicked.cellPhone">
                &middot; Cell {{ formatPhone(rowClicked.cellPhone) }}
              </span>
              <span v-if="rowClicked.date">
                &middot; {{ rowClicked.date | time }}</span
              >
            </div>
            <div
              v-if="accountLinked && rowClicked.address1"
              class="voicemail-dialog-address"
            >
              <v-icon small color="#b0bec5" class="mr-1">mdi-map-marker</v-icon>
              {{ rowClicked.address1 }}
            </div>
          </div>
          <div class="voicemail-dialog-header-actions">
            <v-btn
              v-if="accountLinked"
              outline
              small
              dark
              class="voicemail-unlink-btn"
              :loading="unlinking"
              @click="unlinkAccountDialog = true"
            >
              <v-icon small class="mr-1">mdi-link-variant-off</v-icon>
              Unlink
            </v-btn>
            <v-btn
              icon
              dark
              class="voicemail-dialog-close"
              @click="closeDialog()"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <v-card-text class="voicemail-dialog-body">
          <div v-if="!accountLinked" class="voicemail-link-box mb-3">
            <div class="voicemail-link-title">
              <v-icon small color="#e65100" class="mr-1"
                >mdi-alert-circle</v-icon
              >
              Link to account
            </div>
            <assign-account-lookup
              ref="assignLookup"
              v-model="newAccountID"
              @selected="onAccountSelected"
            ></assign-account-lookup>
            <div class="voicemail-link-actions">
              <v-btn
                color="success"
                small
                :loading="linking"
                :disabled="!newAccountID"
                @click="linkAccount()"
              >
                <v-icon small class="mr-1">mdi-link-variant</v-icon>
                Link account
              </v-btn>
            </div>
          </div>

          <v-layout row wrap>
            <v-flex xs12 md8 class="voicemail-dialog-col-left">
              <v-chip small text-color="white" color="#5e35b1" class="mb-3">
                <v-icon small color="white" class="mr-1">mdi-voicemail</v-icon>
                Voicemail
              </v-chip>

              <voicemail-preview
                v-if="dialog && rowClicked.id"
                :key="rowClicked.id"
                :row="rowClicked"
                :hideAssign="true"
              ></voicemail-preview>
            </v-flex>
            <v-flex xs12 md4 class="voicemail-dialog-col-right">
              <v-select
                v-model="status"
                :items="statusOptions"
                label="Status"
                outline
                hide-details
                dense
                class="mb-3"
              >
                <template slot="selection" slot-scope="data">
                  <v-icon small :color="statusColor(data.item)" class="mr-2">
                    {{ statusIcon(data.item) }}
                  </v-icon>
                  <span>{{ data.item }}</span>
                </template>
                <template slot="item" slot-scope="data">
                  <v-icon small :color="statusColor(data.item)" class="mr-2">
                    {{ statusIcon(data.item) }}
                  </v-icon>
                  <span>{{ data.item }}</span>
                </template>
              </v-select>

              <v-textarea
                v-model="note"
                label="Note"
                outline
                rows="10"
                hide-details
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeDialog()">Cancel</v-btn>
          <v-btn :loading="processing" color="success" dark @click="save()">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unlinkAccountDialog" persistent max-width="420">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-link-variant-off</v-icon>
          Unlink Account
        </v-card-title>
        <v-card-text class="pt-3 pb-2">
          Remove the link between this voicemail and account
          <strong>{{ rowClicked.accountID }}</strong
          >? It will move back to "Unknown account" until it's linked again.
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="unlinkAccountDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="unlinking" @click="confirmUnlink()">
            Unlink
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatPhoneNumber } from "../../utils/helpers.js";

export default {
  provide() {
    return {
      openAssignVoicemail: (row) => this.openDialog(row),
    };
  },
  data: () => ({
    title: "Voicemails",
    url: "voicemails",
    actions: [],
    user: "",
    processing: false,
    linking: false,
    unlinking: false,
    unlinkAccountDialog: false,
    dialog: false,
    rowClicked: {},
    account: {},
    newAccountID: "",
    note: "",
    status: "RECEIVED",
    statusOptions: ["RECEIVED", "COMPLETED"],
    columns: [
      {
        field: "statusIcon",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        icon: () => "mdi-microphone",
        iconColor: (value) => {
          switch ((value || "").toString().trim().toUpperCase()) {
            case "COMPLETED":
              return "success";
            case "RECEIVED":
              return "info";
            default:
              return "#aeb8bf";
          }
        },
      },
      {
        label: "Name",
        field: "name",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Hebrew Name",
        field: "hname",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Account ID",
        field: "accountID",
        link: "Account View",
        template: "link-cell-template",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Date",
        field: "date",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        sortOrder: "desc",
        sortIndex: 0,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        visible: true,
      },
      {
        label: "Caller",
        field: "callerName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Phone",
        field: "phoneNumber",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Status",
        field: "status",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Note",
        field: "note",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Transcript",
        field: "transcriptionStatus",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Cell Phone",
        field: "cellPhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Address",
        field: "address1",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  created() {
    this.user = this.$store.state.authentication.user;

    this.columns.find((c) => c.field == "accountID").showLink = (row) =>
      !!row.accountID;
  },
  computed: {
    accountLinked() {
      return !!(this.rowClicked && this.rowClicked.accountID);
    },
  },
    methods: {
        statusIcon(value) {
            switch ((value || "").toString().trim().toUpperCase()) {
                case "COMPLETED":
                    return "mdi-check-circle";
                case "RECEIVED":
                    return "mdi-inbox-arrow-down";
                default:
                    return "mdi-circle-outline";
            }
        },
        statusColor(value) {
            switch ((value || "").toString().trim().toUpperCase()) {
                case "COMPLETED":
                    return "success";
                case "RECEIVED":
                    return "info";
                default:
                    return "#aeb8bf";
            }
        },
        formatPhone(value) {
      if (!value) return "";
      var digits = (value + "").replace(/\D/g, "");
      if (digits.length === 11 && digits.charAt(0) === "1") {
        digits = digits.substring(1);
      }
      return digits.length === 10 ? formatPhoneNumber(digits) : value;
    },
    onRowClicked(row) {
      this.openDialog(row);
    },
    openDialog(row) {
      this.rowClicked = row;
      this.account = {};
      this.newAccountID = "";
      this.note = row.note || "";
      this.status = this.statusOptions.includes(row.status)
        ? row.status
        : "RECEIVED";
      this.dialog = true;

      this.$nextTick(() => {
        if (this.$refs.assignLookup && this.$refs.assignLookup.clear) {
          this.$refs.assignLookup.clear();
        }
      });
    },
    closeDialog() {
      this.account = {};
      this.newAccountID = "";
      this.note = "";
      this.status = "RECEIVED";
      this.unlinkAccountDialog = false;
      this.dialog = false;
    },
    onAccountSelected(account) {
      this.account = account || {};
      this.newAccountID = account && account.accountID ? account.accountID : "";
    },
    applyLinkedAccount(account) {
      this.rowClicked = Object.assign({}, this.rowClicked, {
        accountID: account.accountID,
        name: account.name || null,
        hname: account.hname || null,
        homePhone: account.homePhone || null,
        cellPhone: account.cellPhone || null,
        address1: account.address1 || null,
      });
      this.account = {};
      this.newAccountID = "";
    },
    applyUnlinkedAccount() {
      this.rowClicked = Object.assign({}, this.rowClicked, {
        accountID: null,
        name: null,
        hname: null,
        homePhone: null,
        cellPhone: null,
        address1: null,
      });
      this.account = {};
      this.newAccountID = "";
    },
    async linkAccount() {
      if (!this.newAccountID) {
        return;
      }

      this.linking = true;

      try {
        await this.$http.post("api/voicemails/save", {
          voicemailID: this.rowClicked.id,
          accountID: this.newAccountID,
          note: this.note,
          status: this.status,
          user: this.user,
        });
        this.applyLinkedAccount(
          this.account && this.account.accountID
            ? this.account
            : { accountID: this.newAccountID }
        );
        this.$refs.grid.reloadGrid();
        this.$notify({
          group: "notification",
          text: "Account linked",
          type: "success",
        });
      } catch (e) {
        this.$notify({
          group: "notification",
          text: "Could not link account",
          type: "error",
        });
      }

      this.linking = false;
    },
    async confirmUnlink() {
      this.unlinking = true;

      try {
        await this.$http.post("api/voicemails/unlinkAccount", {
          voicemailID: this.rowClicked.id,
          user: this.user,
        });
        this.applyUnlinkedAccount();
        this.unlinkAccountDialog = false;
        this.$refs.grid.reloadGrid();
        this.$notify({
          group: "notification",
          text: "Account unlinked",
          type: "success",
        });
      } catch (e) {
        this.$notify({
          group: "notification",
          text: "Could not unlink account",
          type: "error",
        });
      }

      this.unlinking = false;
    },
    async save() {
      this.processing = true;

      try {
        await this.$http.post("api/voicemails/save", {
          voicemailID: this.rowClicked.id,
          accountID: this.accountLinked ? null : this.newAccountID,
          note: this.note,
          status: this.status,
          user: this.user,
        });
      } catch (e) {
        this.processing = false;
        return;
      }

      this.processing = false;
      this.closeDialog();
      this.$refs.grid.reloadGrid();

      this.$notify({
        group: "notification",
        text: "Voicemail saved",
        type: "success",
      });
    },
  },
};
</script>
<style>
.voicemail-dialog-card {
  overflow: hidden;
}

.voicemail-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 14px;
  background: #37474f;
  border-bottom: 3px solid #5e35b1;
}

.voicemail-dialog-header-info {
  min-width: 0;
}

.voicemail-dialog-title {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
}

.voicemail-dialog-title--missing {
  align-items: center;
  color: #ffcc80;
}

.voicemail-dialog-title-he {
  font-size: 14px;
  font-weight: 400;
  color: #cfd8dc;
}

.voicemail-dialog-subtitle {
  font-size: 12px;
  color: #b0bec5;
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.voicemail-dialog-address {
  font-size: 12px;
  color: #b0bec5;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

.voicemail-dialog-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.voicemail-unlink-btn {
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.45) !important;
}

.voicemail-dialog-close {
  margin: -4px -4px 0 0;
}

.voicemail-dialog-body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 16px;
}

.voicemail-dialog-col-left {
  padding-right: 16px;
}

.voicemail-dialog-col-right {
  padding-left: 16px;
  border-left: 1px solid #e0e0e0;
}

@media (max-width: 599px) {
  .voicemail-dialog-col-left {
    padding-right: 0;
  }

  .voicemail-dialog-col-right {
    padding-left: 0;
    border-left: none;
    margin-top: 12px;
  }
}

.voicemail-link-box {
  padding: 10px 12px 12px;
  background: #fff8e1;
  border: 1px solid #ffb74d;
  border-left: 4px solid #fb8c00;
  border-radius: 6px;
}

.voicemail-link-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #e65100;
  margin-bottom: 8px;
}

.voicemail-link-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
</style>
